import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { BrandConfigProvider } from './context/BrandConfigContext';
import { useBrandConfig } from './context/BrandConfigContext';
import AppRoutes from './routes/AppRoutes';
import Preloader from './components/common/Preloader';
import CampaignPausedModal from './components/CampaignPausedModal';
import NoticeBanner from './components/NoticeBanner';

// Bootstrap CSS & JS
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// Swiper CSS (React-friendly carousel)
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Custom CSS (in src/)
import './custom.css';

function TrackingScripts() {
  const brand = useBrandConfig();

  useEffect(() => {
    const tagId = brand?.google_tag_id;
    if (tagId && !document.getElementById('gtag-script')) {
      const s1 = document.createElement('script');
      s1.id = 'gtag-script';
      s1.async = true;
      s1.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
      document.head.appendChild(s1);
      const s2 = document.createElement('script');
      s2.id = 'gtag-init';
      s2.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${tagId}');`;
      document.head.appendChild(s2);
    }
  }, [brand?.google_tag_id]);

  useEffect(() => {
    const pixelId = brand?.fb_pixel_id;
    if (pixelId && !document.getElementById('fb-pixel-script')) {
      const s = document.createElement('script');
      s.id = 'fb-pixel-script';
      s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`;
      document.head.appendChild(s);
    }
  }, [brand?.fb_pixel_id]);

  // Microsoft Clarity — the standard loader, built via the DOM so the ID is never evaluated as code
  useEffect(() => {
    const clarityId = String(brand?.clarity_id || '').trim();
    if (!/^[a-z0-9]+$/i.test(clarityId) || document.getElementById('clarity-script')) return;
    window.clarity =
      window.clarity ||
      function clarity(...args) {
        (window.clarity.q = window.clarity.q || []).push(args);
      };
    const s = document.createElement('script');
    s.id = 'clarity-script';
    s.async = true;
    s.src = `https://www.clarity.ms/tag/${encodeURIComponent(clarityId)}`;
    document.head.appendChild(s);
  }, [brand?.clarity_id]);

  return null;
}

// Landing-page-only popup shown while the campaign is PAUSED
function PausedPopupGate() {
  const location = useLocation();
  if (location.pathname !== '/') return null;
  return <CampaignPausedModal />;
}

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <BrandConfigProvider>
      <AppProvider>
        <Router>
          <TrackingScripts />
          {loading && <Preloader />}
          <PausedPopupGate />
          <NoticeBanner />
          <AppRoutes />
        </Router>
      </AppProvider>
    </BrandConfigProvider>
  );
}

export default App;
